/**
 * Active-view state for a board.
 *
 * A tracker OPENS on the first enabled view (`views[0]`) — the order is the
 * shared default (see `layout.views`). Switching during a session is local and
 * NOT persisted, so a fresh open always lands on the first view again. If the
 * active view is removed, it falls back to the first.
 */
import { useEffect, useState } from 'react';

import type { TrackerViewType } from '@/entities/tracker';

export function useActiveView(views: TrackerViewType[]): {
  activeView: TrackerViewType;
  setActiveView: (view: TrackerViewType) => void;
} {
  // Seeded synchronously from views[0] (the tracker prop is available at mount,
  // so there's no async race that would flash the wrong default).
  const [activeView, setActiveView] = useState<TrackerViewType>(() => views[0] ?? 'table');

  // If the current view leaves the enabled set (removed elsewhere), snap back
  // to the first. Does NOT re-snap on mere reorder — you keep looking at what
  // you're looking at; the new order only decides the NEXT open.
  useEffect(() => {
    if (views.length > 0 && !views.includes(activeView)) {
      setActiveView(views[0]);
    }
  }, [views, activeView]);

  return { activeView, setActiveView };
}
